/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  GroupedEnum,
  IFileUploadQueue,
  IJobType,
  ILaserOrEndmill,
  ILicense,
  IMaterialUsed,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IToolSetter,
  IUnits,
  IXYDatumPosition,
  IZZeroPosition,
  IPostAttachment,
  IToolData,
} from './post.d';
// eslint-disable-next-line import/no-cycle
import { getGroupedOptionsFlat } from './post.utils';

const initialState = {
  projectEditorData: {
    _id: '',
    models: [] as IPostAttachment[],
    mediaFiles: [] as IPostAttachment[],
    thumbnail: null as IPostAttachment | null,
    title: '',
    description: '',
    category: '',
    tags: [],
    originalMachine: '',
    supportedMachines: [],
    softwareUsed: Object.values(ISoftwareUsed)[0],
    materialUsed: Object.values(
      getGroupedOptionsFlat(IMaterialUsed as unknown as GroupedEnum)
    )[0],
    jobType: Object.values(IJobType)[0],
    units: Object.values(IUnits)[0],
    jobSizeHeight: 0.25,
    jobSizeThickness: 0.25,
    jobSizeWidth: 0.25,
    jobSizeDiameter: 0,
    jobSizeLength: 0,
    zZeroPosition: Object.values(IZZeroPosition)[0],
    xyDatumPosition: Object.values(IXYDatumPosition)[0],
    laserOrEndmill: Object.values(ILaserOrEndmill)[0],
    toolData: [] as IToolData[],
    toolSetter: Object.values(IToolSetter)[0],
    probeForZHeightCommand: Object.values(IProbeForZHeightCommand)[0],
    spindleSpeed: 18000,
    youtubeAttachments: [],
    licenceType: Object.values(ILicense)[0],
    isLoading: false,
    isError: false,
    isSuccess: false,
    error: null,
    fileUploadQueue: [] as IFileUploadQueue[],
  },
};

const projectEditorSlice = createSlice({
  name: 'projectEditor',
  initialState,
  reducers: {
    resetProjectEditorData: (state) => {
      state.projectEditorData = initialState.projectEditorData;
    },
    setProjectEditorData: (state, action) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        ...action.payload,
      };
    },
    removeFileFromUploadQueueByCategory: (state, action) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: state.projectEditorData.fileUploadQueue.filter(
          (file) => file.category !== action.payload
        ),
      };
    },
    removeFileFromUploadQueue: (state, action) => {
      const newFileUploadQueue = state.projectEditorData.fileUploadQueue.filter(
        (file) =>
          !(
            file.fileHash === action.payload.fileHash &&
            file.category === action.payload.category
          )
      );
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: newFileUploadQueue,
      };
    },
    removeAllFilesFromUploadQueue: (state) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: [],
      };
    },
    addFileToUploadQueue: (state, action) => {
      const existingFile = state.projectEditorData.fileUploadQueue.find(
        (file) =>
          file.fileHash === action.payload.fileHash &&
          file.category === action.payload.category
      );
      if (existingFile) {
        state.projectEditorData.fileUploadQueue =
          state.projectEditorData.fileUploadQueue.filter(
            (file) =>
              file.fileHash === action.payload.fileHash &&
              file.category === action.payload.category
          );
      }
      state.projectEditorData.fileUploadQueue.push(action.payload);
    },
  },
});

export type IProjectEditorState = typeof initialState.projectEditorData;

export const {
  setProjectEditorData,
  resetProjectEditorData,
  removeFileFromUploadQueue,
  addFileToUploadQueue,
  removeAllFilesFromUploadQueue,
  removeFileFromUploadQueueByCategory,
} = projectEditorSlice.actions;

export default projectEditorSlice.reducer;

export const selectProjectEditorData = (state: {
  projectEditor: {
    projectEditorData: IProjectEditorState;
  };
}) => {
  return state.projectEditor.projectEditorData;
};
